<template>
  <section v-if="history && hasData" class="raise-history">
    <h3 class="raise-history__title">How the Raise Is Going</h3>
    <svg
      class="raise-history__chart"
      :viewBox="`0 0 ${W} ${H}`"
      role="img"
      :aria-label="`Money raised over time: ${short(history.raised)} of a ${short(goal)} goal`"
    >
      <defs>
        <linearGradient :id="gradientId" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#4D84FF" stop-opacity="0.22" />
          <stop offset="100%" stop-color="#4D84FF" stop-opacity="0" />
        </linearGradient>
      </defs>
      <g v-for="tick in yTicks" :key="tick.value">
        <line :x1="PAD.left" :x2="W - PAD.right" :y1="y(tick.value)" :y2="y(tick.value)" :class="tick.goal ? 'raise-history__goal' : 'raise-history__grid'" />
        <text :x="PAD.left - 8" :y="y(tick.value) + 4" text-anchor="end" class="raise-history__axis" :class="{ 'raise-history__axis--goal': tick.goal }">
          {{ tick.label }}
        </text>
      </g>
      <text v-if="goal > 0" :x="PAD.left + 4" :y="y(goal) - 6" class="raise-history__axis raise-history__axis--goal">Goal</text>
      <path :d="areaPath" :fill="`url(#${gradientId})`" />
      <path :d="linePath" class="raise-history__line" />
      <circle v-if="series.length" :cx="x(last.t)" :cy="y(last.v)" r="4" class="raise-history__dot" />
      <text v-for="tick in xTicks" :key="tick.t" :x="x(tick.t)" :y="H - 6" :text-anchor="tick.anchor" class="raise-history__axis">
        {{ tick.label }}
      </text>
    </svg>

    <div class="raise-history__stats">
      <div>
        <span class="raise-history__label">Raised So Far</span>
        <span class="raise-history__value">{{ short(history.raised) }}</span>
        <span v-if="goal > 0" class="raise-history__sub">{{ percentOfGoal }}% of the goal</span>
      </div>
      <div>
        <span class="raise-history__label">Last 7 Days</span>
        <span class="raise-history__value">{{ short(history.last_7_days) }}</span>
      </div>
      <div>
        <span class="raise-history__label">Left to Raise</span>
        <span class="raise-history__value">{{ short(left) }}</span>
        <span v-if="daysToGo !== null" class="raise-history__sub">{{ daysToGo === 1 ? "1 day" : `${daysToGo} days` }} to go</span>
      </div>
    </div>
  </section>
</template>

<script>
import api from "@/utils/api";
import { compact, currencySymbol } from "@/utils/exchange.js";

const DAY_MS = 86400000;
let instance = 0;

/**
 * "How the raise is going" for an offer at a set price: the money raised so far, day by day,
 * against the goal, from GET /token/:id/raise-history. The line ends where the raise bar does
 * (the backend starts it at whatever no trade accounts for).
 */
export default {
  name: "RaiseHistory",
  props: {
    tokenId: { type: String, required: true },
    currency: { type: String, default: "NGN" },
  },
  data() {
    instance += 1;
    return {
      history: null,
      W: 640,
      H: 220,
      PAD: { top: 24, right: 16, bottom: 26, left: 76 },
      gradientId: `raise-history-grad-${instance}`,
    };
  },
  computed: {
    goal() { return Number(this.history?.raise_amount) || 0; },
    hasData() { return Number(this.history?.raised) > 0 || (this.history?.points || []).length > 0; },
    left() { return Math.max(0, this.goal - Number(this.history?.raised || 0)); },
    percentOfGoal() { return this.goal > 0 ? ((Number(this.history.raised) / this.goal) * 100).toFixed(1) : "0"; },
    daysToGo() {
      const closes = this.history?.closes_at ? new Date(this.history.closes_at).getTime() : null;
      if (!closes || closes < Date.now()) return null;
      return Math.max(1, Math.ceil((closes - Date.now()) / DAY_MS));
    },
    // From the offer opening at whatever no trade accounts for, through each day's running total,
    // to today.
    series() {
      if (!this.history) return [];
      const now = Date.now();
      const opened = this.history.opened_at ? new Date(this.history.opened_at).getTime() : null;
      const days = (this.history.points || []).map((p) => ({ t: new Date(`${p.date}T12:00:00Z`).getTime(), v: Number(p.cumulative) }));
      const start = opened && (!days.length || opened < days[0].t) ? opened : (days[0]?.t ?? now) - DAY_MS;
      const points = [{ t: start, v: Number(this.history.opening) || 0 }, ...days];
      const end = Math.max(now, points[points.length - 1].t);
      points.push({ t: end, v: points[points.length - 1].v });
      return points;
    },
    last() { return this.series[this.series.length - 1]; },
    tMin() { return this.series[0]?.t ?? 0; },
    tMax() { return Math.max(this.last?.t ?? 1, this.tMin + DAY_MS); },
    vMax() { return Math.max(this.goal, ...this.series.map((p) => p.v), 1); },
    // The goal's amount goes in the axis gutter like any other tick; the word "Goal" sits on its
    // line inside the chart, since "₦300.0M goal" is wider than the gutter.
    yTicks() {
      const top = this.goal > 0 ? this.goal : this.vMax;
      return [0, top / 3, (2 * top) / 3, top].map((value, i) => ({ value, label: this.short(value), goal: i === 3 && this.goal > 0 }));
    },
    xTicks() {
      const span = this.tMax - this.tMin;
      return [0, 1 / 3, 2 / 3, 1].map((f, i) => ({
        t: this.tMin + span * f,
        label: new Date(this.tMin + span * f).toLocaleDateString("en-GB", { day: "numeric", month: "short" }),
        anchor: i === 0 ? "start" : i === 3 ? "end" : "middle",
      }));
    },
    linePath() {
      return this.series.map((p, i) => `${i ? "L" : "M"}${this.x(p.t).toFixed(1)},${this.y(p.v).toFixed(1)}`).join(" ");
    },
    areaPath() {
      if (!this.series.length) return "";
      const base = this.y(0).toFixed(1);
      return `${this.linePath} L${this.x(this.last.t).toFixed(1)},${base} L${this.x(this.tMin).toFixed(1)},${base} Z`;
    },
  },
  watch: {
    tokenId: { immediate: true, handler: "load" },
  },
  methods: {
    async load() {
      if (!this.tokenId) return;
      try {
        const res = await api.getTokenRaiseHistory(this.tokenId);
        this.history = res.data?.data || null;
      } catch (error) {
        this.history = null;
      }
    },
    x(t) {
      return this.PAD.left + ((t - this.tMin) / (this.tMax - this.tMin)) * (this.W - this.PAD.left - this.PAD.right);
    },
    y(v) {
      return this.PAD.top + (1 - v / this.vMax) * (this.H - this.PAD.top - this.PAD.bottom);
    },
    short(value) {
      return `${currencySymbol(this.currency)}${compact(Number(value) || 0)}`;
    },
  },
};
</script>

<style lang="scss" scoped>
$text-primary: #0f172a;
$text-secondary: #475569;
$text-muted: #94a3b8;
$border-color: #e2e8f0;
$accent: #4d84ff;

.raise-history {
  background: #ffffff;
  border: 1px solid $border-color;
  border-radius: 12px;
  padding: 20px;

  &__title {
    font-size: 15px;
    font-weight: 600;
    color: $text-primary;
    margin: 0 0 12px;
  }

  &__chart {
    width: 100%;
    height: auto;
    display: block;
  }

  &__grid { stroke: $border-color; stroke-width: 1; }
  &__goal { stroke: $text-secondary; stroke-width: 1; stroke-dasharray: 4 4; }
  &__line { fill: none; stroke: $accent; stroke-width: 2.5; stroke-linejoin: round; }
  &__dot { fill: $accent; stroke: #ffffff; stroke-width: 2; }

  &__axis {
    font-size: 11px;
    fill: $text-muted;
    &--goal { fill: $text-secondary; font-weight: 600; }
  }

  &__stats {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    border-top: 1px solid $border-color;
    margin-top: 12px;
    padding-top: 14px;
    > div { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
  }

  &__label { font-size: 12px; color: $text-secondary; }
  &__value { font-size: 18px; font-weight: 600; color: $text-primary; }
  &__sub { font-size: 12px; color: $text-muted; }
}
</style>
